// useBannedWords.ts — Reads + manages the banned word list.
//
// Source of truth: dominus_admin (mirrors to auth_canister on each mutation).
// admin_list_banned_words() is a public query — no identity required for reads.
// add/remove require admin identity (canister enforces, frontend forwards).
//
// All mutations are confirmation-gated in the UI.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import { createAdminAgent, createAdminActor } from '../lib/adminAgent';

export interface UseBannedWordsReturn {
  words: string[];
  isLoading: boolean;
  isMutating: boolean;
  error: string | null;
  mutationError: string | null;
  refresh: () => void;
  addWord: (word: string) => Promise<boolean>;
  removeWord: (word: string) => Promise<boolean>;
}

export function useBannedWords(identity: Identity | null): UseBannedWordsReturn {
  const [words, setWords] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  const fetchWords = useCallback(async () => {
    if (!identity) return;
    setIsLoading(true);
    setError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const list = await actor.admin_list_banned_words();
      // Sort alphabetically for display
      const sorted = [...list].sort((a, b) => a.localeCompare(b));
      if (isMountedRef.current) setWords(sorted);
    } catch (err) {
      if (isMountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch banned words.';
        setError(msg);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [identity]);

  const refresh = useCallback(() => { fetchWords(); }, [fetchWords]);

  useEffect(() => {
    if (!identity) return;
    isMountedRef.current = true;
    fetchWords();
    return () => { isMountedRef.current = false; };
  }, [identity, fetchWords]);

  const addWord = useCallback(async (word: string): Promise<boolean> => {
    if (!identity) return false;
    setIsMutating(true);
    setMutationError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_add_banned_word(word.trim().toLowerCase());
      if ('err' in result) throw new Error(result.err);
      await fetchWords();
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add word.';
      if (isMountedRef.current) setMutationError(msg);
      return false;
    } finally {
      if (isMountedRef.current) setIsMutating(false);
    }
  }, [identity, fetchWords]);

  const removeWord = useCallback(async (word: string): Promise<boolean> => {
    if (!identity) return false;
    setIsMutating(true);
    setMutationError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const result = await actor.admin_remove_banned_word(word);
      if ('err' in result) throw new Error(result.err);
      await fetchWords();
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove word.';
      if (isMountedRef.current) setMutationError(msg);
      return false;
    } finally {
      if (isMountedRef.current) setIsMutating(false);
    }
  }, [identity, fetchWords]);

  return { words, isLoading, isMutating, error, mutationError, refresh, addWord, removeWord };
}
